// Pill that shows the current user's tier (Free / Starter / Pro / Elite).
// Reads `credits_purchased_total` from profiles. If a value is passed via
// the `purchased` prop, we trust it and skip the network call — handy for
// rendering badges on leaderboard rows where we already loaded the data.
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { getUserTier, TIER_META, type UserTier } from "@/lib/user-tier";
import { cn } from "@/lib/utils";

interface Props {
  /** When provided, skips the fetch and uses this value directly. */
  purchased?: number | null;
  /** Optional explicit tier override (e.g. for other users we already classified). */
  tier?: UserTier;
  className?: string;
  /** Render a smaller pill (used inline next to names). */
  size?: "sm" | "md";
}

export const UserTierBadge = ({ purchased, tier, className, size = "md" }: Props) => {
  const { user } = useAuth();
  const [resolved, setResolved] = useState<UserTier | null>(
    tier ?? (purchased !== undefined ? getUserTier(purchased) : null),
  );

  useEffect(() => {
    if (tier || purchased !== undefined || !user) return;
    let active = true;
    supabase
      .from("profiles")
      .select("credits_purchased_total")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (!active) return;
        setResolved(getUserTier(Number(data?.credits_purchased_total ?? 0)));
      });
    return () => { active = false; };
  }, [tier, purchased, user]);

  if (!resolved) return null;
  const meta = TIER_META[resolved];

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border font-display font-bold uppercase tracking-[0.18em]",
        size === "sm" ? "px-2 py-0.5 text-[9px]" : "px-2.5 py-1 text-[10px]",
        meta.className,
        className,
      )}
      title={`${meta.label} tier`}
    >
      <span aria-hidden>{meta.emoji}</span>
      {meta.label}
    </span>
  );
};
